export type InformeFieldType = 'text' | 'date' | 'number';

export interface InformeField {
  id: string;
  label: string;
  type: InformeFieldType;
  getValue: (row: object) => unknown;
}

export type InformeFilterOperator = 'contains' | 'equals' | 'greaterThan' | 'lessThan' | 'between';

export interface InformeFilter {
  id: number;
  fieldId: string;
  operator: InformeFilterOperator;
  value: string;
  valorHasta?: string;
}

export type InformeAggregation = 'sum' | 'average' | 'count' | 'min' | 'max';

export interface InformeSummaryRow {
  groupValue: string;
  value: number;
}

export function filterInformeRows(
  rows: object[],
  filters: InformeFilter[],
  fields: InformeField[],
): object[] {
  const activeFilters = filters
    .filter((filter) => {
      if (!filter.fieldId) return false;
      if (filter.operator === 'between') {
        return Boolean(filter.value.trim() && filter.valorHasta?.trim());
      }
      return Boolean(filter.value.trim());
    })
    .map((filter) => ({
      ...filter,
      field: fields.find((field) => field.id === filter.fieldId),
    }))
    .filter((filter) => filter.field);

  return rows.filter((row) =>
    activeFilters.every(({ field, operator, value, valorHasta }) => {
      if (!field) return true;
      const actual = field.getValue(row);
      if (actual == null) return false;

      if (operator === 'contains') {
        return String(actual).toLocaleLowerCase().includes(value.trim().toLocaleLowerCase());
      }
      if (operator === 'equals') {
        return String(actual).toLocaleLowerCase() === value.trim().toLocaleLowerCase();
      }

      const toComparable = (raw: unknown) => (
        field.type === 'date'
          ? new Date(String(raw)).getTime()
          : field.type === 'number'
            ? Number(raw)
            : Number.NaN
      );
      const actualNumber = toComparable(actual);
      const expectedNumber = toComparable(value);
      if (!Number.isFinite(actualNumber) || !Number.isFinite(expectedNumber)) return false;
      if (operator === 'between') {
        const endNumber = toComparable(valorHasta);
        if (!Number.isFinite(endNumber)) return false;
        return actualNumber >= expectedNumber && actualNumber <= endNumber;
      }
      return operator === 'greaterThan'
        ? actualNumber >= expectedNumber
        : actualNumber <= expectedNumber;
    }),
  );
}

export function summarizeInformeRows(
  rows: object[],
  groupField: InformeField,
  metricField: InformeField,
  aggregation: InformeAggregation,
): InformeSummaryRow[] {
  const groups = new Map<string, number[]>();
  rows.forEach((row) => {
    const rawGroup = groupField.getValue(row);
    const groupValue = rawGroup == null || rawGroup === '' ? '(Sin valor)' : String(rawGroup);
    const values = groups.get(groupValue) ?? [];
    const metricValue = Number(metricField.getValue(row));
    if (aggregation === 'count') {
      values.push(1);
    } else if (Number.isFinite(metricValue)) {
      values.push(metricValue);
    }
    groups.set(groupValue, values);
  });

  return Array.from(groups, ([groupValue, values]) => {
    let value = 0;
    if (aggregation === 'count' || aggregation === 'sum') {
      value = values.reduce((total, item) => total + item, 0);
    } else if (aggregation === 'average') {
      value = values.length ? values.reduce((total, item) => total + item, 0) / values.length : 0;
    } else if (aggregation === 'min') {
      value = values.length ? Math.min(...values) : 0;
    } else if (aggregation === 'max') {
      value = values.length ? Math.max(...values) : 0;
    }
    return { groupValue, value };
  });
}
